import { useCallback } from 'react';
import logger from '@/app/logger';
import { toast } from 'sonner';
import { IMAGE_VALIDATION, validateImageCount } from '@/lib/validation';

export interface UseFilePasteOptions {
  /**
   * Current count of files already attached (for count validation)
   */
  currentFileCount: number;
  /**
   * Function to upload multiple files and return their URLs
   */
  uploadMultipleFiles: (files: File[]) => Promise<string[]>;
  /**
   * Callback when files are uploaded successfully
   */
  onFilesUploaded: (urls: string[]) => void;
  /**
   * Whether paste should be disabled (e.g., while streaming)
   */
  disabled?: boolean;
  /**
   * Optional log context for debugging (e.g., 'chat', 'home new project')
   */
  logContext?: string;
  /**
   * Optional file filter function (e.g., to accept only images)
   * Defaults to accepting all files
   */
  fileFilter?: (file: File) => boolean;
  /**
   * Maximum number of files allowed (for count validation)
   * Defaults to IMAGE_VALIDATION.maxCount
   */
  maxCount?: number;
}

/**
 * Custom hook that encapsulates file paste logic.
 * Handles clipboard events, extracts files, validates count, uploads, and updates state.
 *
 * @param options - Configuration options for paste handling
 * @returns Paste event handler callback
 */
export function useFilePaste({
  currentFileCount,
  uploadMultipleFiles,
  onFilesUploaded,
  disabled = false,
  logContext = 'paste',
  fileFilter,
  maxCount = IMAGE_VALIDATION.maxCount,
}: UseFilePasteOptions) {
  const handlePaste = useCallback(
    async (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      // Don't process paste if disabled
      if (disabled) {
        return;
      }

      const items = Array.from(event.clipboardData.items);

      // Extract file items
      const fileItems = items.filter((item) => item.kind === 'file');

      // If there are files, prevent default to handle them manually
      if (fileItems.length > 0) {
        event.preventDefault();

        // Extract files
        let files = fileItems
          .map((item) => item.getAsFile())
          .filter((file): file is File => file !== null);

        // Apply file filter if provided
        if (fileFilter) {
          files = files.filter(fileFilter);
        }

        logger.debug(
          { fileCount: files.length, context: logContext },
          'Files pasted'
        );

        // Validate count
        const { isValid, allowedCount, exceededCount } = validateImageCount(
          currentFileCount,
          files.length,
          maxCount
        );

        if (!isValid) {
          logger.warn(
            {
              maxCount,
              attemptedCount: files.length,
              exceededCount,
              context: logContext,
            },
            'File count limit exceeded'
          );
          toast.error(
            `Maximum ${maxCount} files allowed. ${exceededCount} file${
              exceededCount > 1 ? 's' : ''
            } omitted.`
          );
        }

        // Take only allowed count
        const filesToUpload = files.slice(0, allowedCount);

        // Upload files
        const uploadedUrls = await uploadMultipleFiles(filesToUpload);

        logger.debug({ uploadedCount: uploadedUrls.length, context: logContext }, 'Files uploaded successfully');

        // Update state with uploaded URLs
        if (uploadedUrls.length > 0) {
          onFilesUploaded(uploadedUrls);
        }
      }

      // Note: Text paste is handled by default browser behavior
    },
    [disabled, currentFileCount, maxCount, fileFilter, uploadMultipleFiles, onFilesUploaded, logContext]
  );

  return handlePaste;
}
